import React, { useState } from 'react'
import classNames from 'classnames'

import css from './ThumbnailRow.styles.scss'

import {
  ExcessQuantityProps,
  QuantifiableThumbnailComponentProps,
  QuantifiableThumbnailProps,
  ThumbnailRowProps,
} from './ThumbnailRow.types'
import Badge from '../Badge/Badge'
import Play from '../../icons/Play.svg'

const QuantifiableThumbnail: React.FC<QuantifiableThumbnailComponentProps> = ({
  imageAlt,
  imageSrc,
  onClick,
  quantity,
  strike,
  selectable = false,
  selected = false,
  video = false,
}) => {
  const QuantifiableThumbnailClasses = classNames(css.QuantifiableThumbnail, {
    [css['QuantifiableThumbnail--selectable']]: selectable,
    [css['QuantifiableThumbnail--selected']]: selected,
  })

  const handleClick = () => {
    if (!selectable) return
    onClick && onClick()
  }

  const strikeClasses = classNames(css.QuantifiableThumbnail__strike, {
    [css['QuantifiableThumbnail__strike--dark']]: strike === 'dark',
    [css['QuantifiableThumbnail__strike--light']]: strike === 'light',
  })

  return (
    <div className={QuantifiableThumbnailClasses} onClick={handleClick}>
      <div className={css.ImageContainer}>
        <img
          src={imageSrc}
          alt={imageAlt}
          tabIndex={selectable ? 0 : undefined}
        />
        {strike && <div className={strikeClasses} data-testid="strike"></div>}
        {video && (
          <div
            className={css.QuantifiableThumbnail__video}
            data-testid="video-icon"
          >
            <Play className={css.QuantifiableThumbnail__video__icon} />
          </div>
        )}
      </div>
      {quantity > 1 && (
        <div className={css.QuantityBadgeContainer}>
          <Badge
            fill="default"
            quantity={quantity}
            size="small"
            variant="quantity"
          />
        </div>
      )}
    </div>
  )
}

const ExcessQuantity: React.FC<ExcessQuantityProps> = ({
  excess,
  selectable = false,
}) => {
  const ExcessQuantityClasses = classNames(css.ExcessQuantity, {
    [css['ExcessQuantity--selectable']]: selectable,
  })

  return (
    <div
      className={ExcessQuantityClasses}
      tabIndex={selectable ? 0 : undefined}
    >
      <span>{excess > 99 ? '99+' : `+${excess}`}</span>
    </div>
  )
}

const ThumbnailRow: React.FC<ThumbnailRowProps> = ({
  className,
  thumbnails,
  maxVisible,
  onSelectionChanged,
  responsive = false,
  selectable = false,
  size,
}) => {
  const [selected, setSelected] = useState<QuantifiableThumbnailProps>()

  const ThumbnailRowClasses = classNames(css.ThumbnailRow, className, {
    [css['ThumbnailRow--small']]: size === 'sm',
    [css['ThumbnailRow--medium']]: size === 'md',
    [css['ThumbnailRow--responsive']]: responsive,
  })

  const thumbnailsToShow = maxVisible
    ? thumbnails.slice(0, maxVisible)
    : thumbnails

  const handleSelectionChange = (thumbnail: QuantifiableThumbnailProps) => {
    setSelected(thumbnail)
    onSelectionChanged && onSelectionChanged(thumbnail)
  }

  return (
    <div className={ThumbnailRowClasses}>
      {thumbnailsToShow.map((thumbnail, index) => (
        <QuantifiableThumbnail
          {...thumbnail}
          key={index}
          selectable={selectable}
          selected={selected === thumbnail}
          onClick={() => handleSelectionChange(thumbnail)}
        />
      ))}
      {maxVisible && thumbnails.length > maxVisible && (
        <ExcessQuantity
          excess={thumbnails.length - maxVisible}
          selectable={selectable}
        />
      )}
    </div>
  )
}

export default ThumbnailRow
